import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { SystemAdminService } from '@/services/api/system-admin';
import { AppFeedbackCategory, CreateTenantDto, UpdateAppFeedbackReviewSettingsDto, UpdateTenantDto } from 'breuer-supercharged-sdk';
import { fetchMyTenants, MY_TENANTS_QUERY_KEY } from '@/hooks/useTenants';
import { useTenantStore } from '@/state/tenant_state';

// Get all tenants
export function useTenants() {
    return useQuery({
        queryKey: ['system-admin', 'tenants'],
        queryFn: async () => {
            return SystemAdminService.getInstance().getAllTenants();
        },
    });
}

// Get tenant users
export function useTenantUsers(tenantId: string | null) {
    return useQuery({
        queryKey: ['system-admin', 'tenant-users', tenantId],
        queryFn: async () => {
            return SystemAdminService.getInstance().getTenantUsers(tenantId!);
        },
        enabled: !!tenantId,
    });
}

// Get billing statistics
export function useBillingStatistics(startDate?: string, endDate?: string) {
    return useQuery({
        queryKey: ['system-admin', 'billing-statistics', startDate, endDate],
        queryFn: async () => {
            return SystemAdminService.getInstance().getAllTenantsBillingStatistics(startDate, endDate);
        },
    });
}

// Get tenant billing history
export function useTenantBillingHistory(tenantId: string | null, startDate?: string, endDate?: string) {
    return useQuery({
        queryKey: ['system-admin', 'tenant-billing-history', tenantId, startDate, endDate],
        queryFn: async () => {
            return SystemAdminService.getInstance().getTenantBillingHistory(tenantId!, startDate, endDate);
        },
        enabled: !!tenantId,
    });
}

// In-app feedback of all tenants, newest first (server-side paging, light
// entries — logs and signed file URLs come from useAppFeedbackDetail).
export function useAppFeedbackList(params: {
    page: number;
    pageSize: number;
    tenantId?: string;
    category?: AppFeedbackCategory;
}) {
    const { page, pageSize, tenantId, category } = params;
    return useQuery({
        queryKey: ['system-admin', 'app-feedback', page, pageSize, tenantId ?? null, category ?? null],
        queryFn: async () => {
            return SystemAdminService.getInstance().listAppFeedback({
                limit: pageSize,
                offset: (page - 1) * pageSize,
                tenantId,
                category,
            });
        },
        placeholderData: keepPreviousData,
    });
}

// One feedback entry for the detail drawer. Its screenshot/attachment URLs are
// signed for 60 min: kept fresh for 30 min (no image reload on every window
// focus) and re-signed every 45 min while the drawer stays open.
const APP_FEEDBACK_DETAIL_STALE_MS = 30 * 60 * 1000;
const APP_FEEDBACK_DETAIL_RESIGN_MS = 45 * 60 * 1000;

export function useAppFeedbackDetail(feedbackId: string | null) {
    return useQuery({
        queryKey: ['system-admin', 'app-feedback-detail', feedbackId],
        queryFn: async () => {
            return SystemAdminService.getInstance().getAppFeedback(feedbackId!);
        },
        enabled: !!feedbackId,
        staleTime: APP_FEEDBACK_DETAIL_STALE_MS,
        refetchInterval: APP_FEEDBACK_DETAIL_RESIGN_MS,
    });
}

// Mutations for tenant management
export function useCreateTenant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (tenantData: CreateTenantDto) => {
            return SystemAdminService.getInstance().createTenant(tenantData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-admin', 'tenants'] });
        },
    });
}

export function useUpdateTenant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ tenantId, tenantData }: { tenantId: string; tenantData: UpdateTenantDto }) => {
            return SystemAdminService.getInstance().updateTenant(tenantId, tenantData);
        },
        onSuccess: async (_tenant, { tenantId }) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['system-admin', 'tenants'] }),
                queryClient.invalidateQueries({ queryKey: MY_TENANTS_QUERY_KEY }),
            ]);
            // Admin edited their own selected tenant: update menu/start page now. Never setSelectedTenant (it clears the cache).
            const { selectedTenant, refreshSelectedTenant } = useTenantStore.getState();
            if (selectedTenant?.tenantId !== tenantId) return;
            const fresh = await queryClient
                .fetchQuery({ queryKey: MY_TENANTS_QUERY_KEY, queryFn: fetchMyTenants, staleTime: 1000 })
                .catch(() => null);
            if (fresh) refreshSelectedTenant(fresh);
        },
    });
}

export function useDeleteTenant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (tenantId: string) => {
            return SystemAdminService.getInstance().deleteTenant(tenantId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-admin', 'tenants'] });
        },
    });
}

export function useBlockTenant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ tenantId, isBlocked }: { tenantId: string; isBlocked: boolean }) => {
            return SystemAdminService.getInstance().blockTenant(tenantId, isBlocked);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-admin', 'tenants'] });
        },
    });
}

const feedbackReviewKey = (tenantId: string) => ['system-admin', 'app-feedback-review', tenantId] as const;

export function useAdminFeedbackReviewSettings(tenantId: string) {
    return useQuery({
        queryKey: [...feedbackReviewKey(tenantId), 'settings'],
        queryFn: () => SystemAdminService.getInstance().getFeedbackReviewSettings(tenantId),
    });
}

export function useAdminFeedbackReviewCandidates(tenantId: string) {
    return useQuery({
        queryKey: [...feedbackReviewKey(tenantId), 'candidates'],
        queryFn: () => SystemAdminService.getInstance().listFeedbackReviewCandidates(tenantId),
    });
}

export function useUpdateAdminFeedbackReviewSettings(tenantId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (dto: UpdateAppFeedbackReviewSettingsDto) =>
            SystemAdminService.getInstance().updateFeedbackReviewSettings(tenantId, dto),
        onSuccess: (data) => {
            queryClient.setQueryData([...feedbackReviewKey(tenantId), 'settings'], data);
            if (data.autoReleasedCount > 0) {
                void queryClient.invalidateQueries({ queryKey: ['system-admin', 'app-feedback'] });
            }
        },
    });
}
